import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as analytics from '../services/analytics';

const AdminAuthContext = createContext(null);
const BASE = process.env.REACT_APP_BACKEND_URL;

export function AdminAuthProvider({ children }) {
  // sessionStorage: cleared automatically when the browser tab closes — never persists across sessions
  const [token, setToken] = useState(() => sessionStorage.getItem('adminToken'));
  const [admin, setAdmin] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('adminUser')); } catch { return null; }
  });
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const login = (tok, user) => {
    sessionStorage.setItem('adminToken', tok);
    sessionStorage.setItem('adminUser', JSON.stringify(user));
    setToken(tok);
    setAdmin(user);
    analytics.identify(user?.id);
    analytics.track('admin_login', { admin_role: user?.admin_role || 'unknown' });
  };

  const logout = async () => {
    analytics.track('admin_logout');
    analytics.clearIdentity();
    // Revoke token server-side first (adds to denylist), then clear local state
    if (token) {
      try {
        await axios.post(`${BASE}/api/admin/logout`, {}, { headers: headers() });
      } catch {
        // Proceed with local logout even if the server call fails
      }
    }
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminUser');
    setToken(null);
    setAdmin(null);
    setPermissions([]);
    setRole(null);
  };

  // Fetch the admin's role + effective permissions after login. We refetch
  // whenever the token changes so role updates in one session take effect.
  useEffect(() => {
    if (!token) { setPermissions([]); setRole(null); return; }
    let cancelled = false;
    axios.get(`${BASE}/api/admin/me/permissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (cancelled) return;
        setPermissions(res.data.permissions || []);
        setRole({
          key: res.data.admin_role,
          label: res.data.role_label,
        });
      })
      .catch(() => { /* token bad or not admin — existing guards will handle */ });
    return () => { cancelled = true; };
  }, [token]);

  const hasPermission = useCallback(
    (perm) => !!perm && permissions.includes(perm),
    [permissions]
  );

  const authGet    = (url, cfg)  => axios.get(`${BASE}${url}`,        { headers: headers(), ...(cfg || {}) });
  const authPost   = (url, data) => axios.post(`${BASE}${url}`, data, { headers: headers() });
  const authPatch  = (url, data) => axios.patch(`${BASE}${url}`, data,{ headers: headers() });
  const authDelete = (url)       => axios.delete(`${BASE}${url}`,     { headers: headers() });

  const updateAdmin = (user) => {
    sessionStorage.setItem('adminUser', JSON.stringify(user));
    setAdmin(user);
  };

  return (
    <AdminAuthContext.Provider value={{
      token, admin, role, permissions, hasPermission,
      login, logout, authGet, authPatch, authPost, authDelete, updateAdmin,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => useContext(AdminAuthContext);
